import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { TrendingUp } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { Panel, Field, FormRow, ErrorState, fmtDate, money } from './design';

// Price changes: a new price applies from a date, after approval and (for an increase) notice to
// participants. Approving drafts a new agreement version for each participant who uses the service,
// to send and record their agreement. A third increase within 12 months is flagged.

export function PriceChangeForm({ item, onDone, onCancel }) {
  const [f, setF] = useState({ newPrice: '', effectiveFrom: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/aged-care/service-items/${item.id}/price-change`, { newPrice: Number(f.newPrice), effectiveFrom: f.effectiveFrom, reason: f.reason });
      toast.success('Price change proposed. It applies once approved.');
      if (r.data.warning) toast(r.data.warning, { duration: 8000 });
      onDone();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not propose it'); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2 text-[13px]">
      <p className="text-slate-700">Change the price of <b>{item.name}</b> (now {item.published_price != null ? money(item.published_price) : 'not set'}).</p>
      <FormRow cols={3}>
        <Field label="New price ($)">{(id) => <input id={id} type="number" min="0" step="0.01" className="input-field" value={f.newPrice} onChange={set('newPrice')} />}</Field>
        <Field label="Applies from">{(id) => <input id={id} type="date" className="input-field" value={f.effectiveFrom} onChange={set('effectiveFrom')} />}</Field>
        <Field label="Why">{(id) => <input id={id} className="input-field" value={f.reason} onChange={set('reason')} placeholder="For example: award wage increase" />}</Field>
      </FormRow>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn-primary btn-sm" disabled={busy || !(Number(f.newPrice) > 0) || !f.effectiveFrom} onClick={save}>Propose</button>
      </div>
    </div>
  );
}

export default function PriceChanges({ refreshKey, onChanged }) {
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  // A failed load used to show nothing at all, which reads as "no changes waiting".
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = useCallback(() => {
    api.get('/aged-care/price-changes')
      .then((r) => { setRows(r.data || []); setError(null); })
      .catch((e) => setError(e.response?.data?.message || 'Could not load the price changes'));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const act = async (pc, action) => {
    if (action === 'approve') {
      // Approving is what reaches participants: every one who has used this service in the last
      // 90 days gets a new agreement version drafted to send.
      const ok = await confirm(
        `${pc.name} goes from ${pc.old_price != null ? money(pc.old_price) : 'no price'} to ${money(pc.new_price)} from ${fmtDate(pc.effective_from)}. Approving drafts a new agreement version for every participant who has used this service in the last 90 days, for you to send and record their agreement.`,
        { title: 'Approve this price change?', confirmLabel: 'Approve change' },
      );
      if (!ok) return;
    }
    setBusyId(pc.id);
    try {
      const r = await api.post(`/aged-care/price-changes/${pc.id}/${action}`);
      toast.success(action === 'approve' ? `Approved. ${r.data.agreements || 0} agreement version${r.data.agreements === 1 ? '' : 's'} drafted to send.` : 'Price change withdrawn');
      load(); if (onChanged) onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not update it'); }
    finally { setBusyId(null); }
  };
  if (error) {
    return (
      <Panel title="Price changes" icon={TrendingUp} className="mb-5">
        <ErrorState message={error} onRetry={load} />
      </Panel>
    );
  }
  const recent = rows.filter((r) => r.status === 'proposed' || (r.status === 'approved' && String(r.effective_from).slice(0, 10) >= new Date(Date.now() - 90 * 864e5).toISOString().slice(0, 10)));
  if (!recent.length) return null;
  return (
    <Panel title="Price changes" icon={TrendingUp} className="mb-5" bodyClass="divide-y divide-slate-100">
      {recent.map((pc) => (
        <div key={pc.id} className="px-4 py-2.5 text-[13px] flex flex-wrap items-baseline justify-between gap-2">
          <span className="min-w-0">
            <span className="text-slate-900 font-medium">{pc.name}</span>
            <span className="text-slate-600"> · {pc.old_price != null ? money(pc.old_price) : 'not set'} to {money(pc.new_price)} from {fmtDate(pc.effective_from)}</span>
            {pc.reason && <span className="text-slate-500"> · {pc.reason}</span>}
            {pc.warning && <span className="block text-amber-800">{pc.warning}</span>}
            {pc.status === 'approved' && <span className="block text-slate-500">Approved{pc.approved_by_name ? ` by ${pc.approved_by_name}` : ''}; {pc.agreements} agreement version{pc.agreements === 1 ? '' : 's'} to send (Funding, each participant).</span>}
          </span>
          {pc.status === 'proposed' ? (
            <span className="flex gap-2">
              <button type="button" className="btn-primary btn-sm" disabled={busyId === pc.id} onClick={() => act(pc, 'approve')}>Approve</button>
              {/* Not "Cancel": beside a form that also has Cancel, it read as closing the form. */}
              <button type="button" className="btn-ghost btn-sm" disabled={busyId === pc.id} onClick={() => act(pc, 'cancel')}>Withdraw this change</button>
            </span>
          ) : <span className="badge badge-green">Approved</span>}
        </div>
      ))}
    </Panel>
  );
}
